import { useMemo, useState } from 'react';
import { Copy, Check } from 'lucide-react';

/* ════════════════════════════════════════════════════════════════
 *  UTM 빌더 — 캠페인 추적용 URL 생성기
 *  GA4 의 utm_* 파라미터로 자동 집계되어 보고서 → 트래픽 획득 에 노출됨.
 * ════════════════════════════════════════════════════════════════ */

const SITE_DEFAULT_URL = 'https://community.insidemation.com';
const CUSTOM = '__custom__';

const SOURCE_OPTIONS = [
  { value: 'instagram', label: 'instagram' },
  { value: 'naver_cafe', label: 'naver_cafe' },
  { value: 'twitter', label: 'twitter' },
  { value: 'kakao', label: 'kakao' },
  { value: 'direct', label: 'direct' },
  { value: CUSTOM, label: '직접입력' },
];

const MEDIUM_OPTIONS = [
  { value: 'social', label: 'social' },
  { value: 'community', label: 'community' },
  { value: 'email', label: 'email' },
  { value: 'banner', label: 'banner' },
  { value: CUSTOM, label: '직접입력' },
];

const CAMPAIGN_OPTIONS = [
  { value: 'launch', label: 'launch' },
  { value: 'event', label: 'event' },
  { value: 'notice', label: 'notice' },
  { value: CUSTOM, label: '직접입력' },
];

interface UtmPreset {
  label: string;
  source: string;
  medium: string;
  campaign: string;
}

const PRESETS: UtmPreset[] = [
  { label: '인스타그램 홍보', source: 'instagram', medium: 'social', campaign: 'launch' },
  { label: '네이버 카페', source: 'naver_cafe', medium: 'community', campaign: 'launch' },
  { label: '카카오톡 공유', source: 'kakao', medium: 'social', campaign: 'launch' },
  { label: '트위터/X', source: 'twitter', medium: 'social', campaign: 'launch' },
];

function buildUtmUrl(
  base: string,
  source: string,
  medium: string,
  campaign: string,
  content: string,
): string {
  if (!base.trim()) return '';
  let url: URL;
  try {
    url = new URL(base.trim());
  } catch {
    return '잘못된 URL';
  }
  if (source) url.searchParams.set('utm_source', source);
  if (medium) url.searchParams.set('utm_medium', medium);
  if (campaign) url.searchParams.set('utm_campaign', campaign);
  if (content.trim()) url.searchParams.set('utm_content', content.trim());
  return url.toString();
}

export function UtmBuilderPage() {
  const [baseUrl, setBaseUrl] = useState(SITE_DEFAULT_URL);

  const [sourceSel, setSourceSel] = useState('instagram');
  const [sourceCustom, setSourceCustom] = useState('');
  const [mediumSel, setMediumSel] = useState('social');
  const [mediumCustom, setMediumCustom] = useState('');
  const [campaignSel, setCampaignSel] = useState('launch');
  const [campaignCustom, setCampaignCustom] = useState('');
  const [content, setContent] = useState('');

  const [copied, setCopied] = useState(false);

  /* select 가 '직접입력'이면 custom 값을 사용, 그 외엔 select 값 그대로 */
  const source = sourceSel === CUSTOM ? sourceCustom : sourceSel;
  const medium = mediumSel === CUSTOM ? mediumCustom : mediumSel;
  const campaign = campaignSel === CUSTOM ? campaignCustom : campaignSel;

  const generated = useMemo(
    () => buildUtmUrl(baseUrl, source, medium, campaign, content),
    [baseUrl, source, medium, campaign, content],
  );

  function applyPreset(p: UtmPreset) {
    setSourceSel(SOURCE_OPTIONS.some((o) => o.value === p.source) ? p.source : CUSTOM);
    setSourceCustom(SOURCE_OPTIONS.some((o) => o.value === p.source) ? '' : p.source);
    setMediumSel(MEDIUM_OPTIONS.some((o) => o.value === p.medium) ? p.medium : CUSTOM);
    setMediumCustom(MEDIUM_OPTIONS.some((o) => o.value === p.medium) ? '' : p.medium);
    setCampaignSel(CAMPAIGN_OPTIONS.some((o) => o.value === p.campaign) ? p.campaign : CUSTOM);
    setCampaignCustom(CAMPAIGN_OPTIONS.some((o) => o.value === p.campaign) ? '' : p.campaign);
  }

  async function handleCopy() {
    if (!generated || generated === '잘못된 URL') return;
    try {
      await navigator.clipboard.writeText(generated);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard 미지원/거부 — 무시 (수동 복사 가능) */
    }
  }

  return (
    <div className="w-full">
      <div className="mb-6">
        <h1 className="text-2xl font-extrabold">UTM 빌더</h1>
        <p className="mt-1 text-xs text-[var(--color-text-sub)]">
          외부 채널 유입 추적용 URL을 생성합니다. 생성된 URL의 utm_* 파라미터는 GA4 보고서에 자동 집계됩니다.
        </p>
      </div>

      <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5">
        {/* 프리셋 */}
        <div className="mb-5">
          <div className="mb-2 text-xs font-semibold text-[var(--color-text-sub)]">자주 쓰는 프리셋</div>
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <button
                key={p.label}
                type="button"
                onClick={() => applyPreset(p)}
                className="rounded-full border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-1.5 text-xs hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]"
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* 입력 필드 */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* 기본 URL */}
          <div className="md:col-span-2">
            <label className="mb-1 block text-xs font-semibold text-[var(--color-text-sub)]">
              기본 URL
            </label>
            <input
              type="url"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder={SITE_DEFAULT_URL}
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm focus:border-[var(--color-primary)] focus:outline-none"
            />
          </div>

          {/* 소스 */}
          <SelectWithCustom
            label="소스 (utm_source)"
            options={SOURCE_OPTIONS}
            selValue={sourceSel}
            customValue={sourceCustom}
            onSelChange={setSourceSel}
            onCustomChange={setSourceCustom}
            customPlaceholder="예: blog, podcast …"
          />

          {/* 매체 */}
          <SelectWithCustom
            label="매체 (utm_medium)"
            options={MEDIUM_OPTIONS}
            selValue={mediumSel}
            customValue={mediumCustom}
            onSelChange={setMediumSel}
            onCustomChange={setMediumCustom}
            customPlaceholder="예: cpc, push …"
          />

          {/* 캠페인 */}
          <SelectWithCustom
            label="캠페인 (utm_campaign)"
            options={CAMPAIGN_OPTIONS}
            selValue={campaignSel}
            customValue={campaignCustom}
            onSelChange={setCampaignSel}
            onCustomChange={setCampaignCustom}
            customPlaceholder="예: blackfriday, newyear …"
          />

          {/* 콘텐츠 — 옵션 */}
          <div>
            <label className="mb-1 block text-xs font-semibold text-[var(--color-text-sub)]">
              콘텐츠 (utm_content){' '}
              <span className="ml-1 font-normal text-[var(--color-text-sub)]">선택</span>
            </label>
            <input
              type="text"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="예: top_banner, footer_link …"
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm focus:border-[var(--color-primary)] focus:outline-none"
            />
          </div>
        </div>

        {/* 결과 URL */}
        <div className="mt-5 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] p-3">
          <div className="mb-2 flex items-center justify-between gap-2">
            <span className="text-xs font-semibold text-[var(--color-text-sub)]">생성된 URL</span>
            <button
              type="button"
              onClick={handleCopy}
              disabled={!generated || generated === '잘못된 URL'}
              className="inline-flex items-center gap-1.5 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1.5 text-xs font-semibold hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {copied ? (
                <>
                  <Check className="h-3.5 w-3.5" />
                  복사됨
                </>
              ) : (
                <>
                  <Copy className="h-3.5 w-3.5" />
                  복사
                </>
              )}
            </button>
          </div>
          <code className="block break-all text-sm leading-relaxed text-[var(--color-text)]">
            {generated || (
              <span className="text-[var(--color-text-sub)]">URL이 비어있어요.</span>
            )}
          </code>
        </div>
      </section>
    </div>
  );
}

interface SelectWithCustomProps {
  label: string;
  options: Array<{ value: string; label: string }>;
  selValue: string;
  customValue: string;
  onSelChange: (v: string) => void;
  onCustomChange: (v: string) => void;
  customPlaceholder?: string;
}

function SelectWithCustom({
  label,
  options,
  selValue,
  customValue,
  onSelChange,
  onCustomChange,
  customPlaceholder,
}: SelectWithCustomProps) {
  return (
    <div>
      <label className="mb-1 block text-xs font-semibold text-[var(--color-text-sub)]">
        {label}
      </label>
      <select
        value={selValue}
        onChange={(e) => onSelChange(e.target.value)}
        className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm focus:border-[var(--color-primary)] focus:outline-none"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {selValue === CUSTOM && (
        <input
          type="text"
          value={customValue}
          onChange={(e) => onCustomChange(e.target.value)}
          placeholder={customPlaceholder}
          className="mt-2 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm focus:border-[var(--color-primary)] focus:outline-none"
        />
      )}
    </div>
  );
}
